import { AttachmentType } from 'lib/enum/attachment-type.enum';
import { toast } from 'react-toastify';
import privateFetch from 'services/Api/ApiService/PrivateFetch';

export const readImageToString = (file: File): Promise<string> =>
    new Promise(resolve => {
        const reader = new FileReader();
        reader.addEventListener('load', () => resolve(reader.result as string), false);
        reader.readAsDataURL(file);
    });

export const blobToFile = (theBlob: Blob, fileName: string): File =>
    new File([theBlob], fileName, { lastModified: new Date().getTime() });

export const downloadFileByURL = (url: string, name: string): void => {
    const link = document.createElement('a');
    link.download = name;
    link.href = url;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const downloadSecureAssetByURL = async (
    url: string,
    filename: string,
    type: AttachmentType,
    mimeType?: string,
    blob?: Blob | null,
): Promise<void> => {
    let _blob: Blob | null = blob ?? null;
    let blobUrl: string | null = null;
    let _filename = filename;
    let resolvedMimeType: string | null = '';

    try {
        _filename = _filename.trim().split(' ').join('-');
    } catch {
        _filename = '';
    }

    switch (type) {
        case AttachmentType.File: {
            _filename = 'file';
            resolvedMimeType = mimeType ?? null;
            break;
        }
        case AttachmentType.Image: {
            _filename = 'image';
            resolvedMimeType = mimeType ?? 'image/jpeg';
            break;
        }
        case AttachmentType.Video: {
            _filename = 'video';
            resolvedMimeType = 'video/mp4';
            break;
        }
    }

    if (_blob) {
        if (resolvedMimeType) {
            _blob = new Blob([_blob], { type: mimeType });
        }
        blobUrl = URL.createObjectURL(_blob);
    } else {
        await privateFetch
            .fetch(url)
            .then(async response => response?.blob())
            .then(blob => {
                _blob = blob ?? null;
                if (resolvedMimeType && blob) {
                    _blob = new Blob([blob], { type: resolvedMimeType });
                }
                blobUrl = _blob ? URL.createObjectURL(_blob) : null;
            })
            .catch(() => {
                toast.warning('Failed to download. Please try again.');
            });
    }

    if (blobUrl) {
        const link = document.createElement('a');
        link.download = _filename;
        link.href = blobUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } else {
        toast.warning('Invalid file url. Please try again.');
    }
};

export const inlineCrossOriginImages = async (element: HTMLElement): Promise<HTMLElement> => {
    const images = element.querySelectorAll('img');
    const videos = element.querySelectorAll('video[poster]');
    const imagePromises: Promise<void>[] = [];

    images.forEach(img => {
        const src = img.src;

        if (!src || src.startsWith('data:')) {
            return;
        }

        const promise = (async (): Promise<void> => {
            try {
                const response = await fetch(src);
                if (!response.ok) {
                    return;
                }

                const blob = await response.blob();

                const reader = new FileReader();
                const base64 = await new Promise<string>((resolve, reject): void => {
                    reader.onloadend = (): void => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });

                img.src = base64;
            } catch (error) {
                console.warn(`Failed to inline image: ${src}`, error);
            }
        })();

        imagePromises.push(promise);
    });

    videos.forEach(video => {
        const posterUrl = video.getAttribute('poster');

        if (!posterUrl || posterUrl.startsWith('data:')) {
            return;
        }

        const promise = (async (): Promise<void> => {
            try {
                const response = await fetch(posterUrl);
                if (!response.ok) {
                    return;
                }

                const blob = await response.blob();

                const reader = new FileReader();
                const base64 = await new Promise<string>((resolve, reject): void => {
                    reader.onloadend = (): void => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });

                const wrapper = document.createElement('div');
                wrapper.style.position = 'relative';
                wrapper.style.display = 'inline-block';
                wrapper.style.width = '100%';

                const img = document.createElement('img');
                img.src = base64;
                img.style.width = '100%';
                img.style.height = 'auto';
                img.style.display = 'block';

                const playButton = document.createElement('div');
                playButton.style.position = 'absolute';
                playButton.style.top = '50%';
                playButton.style.left = '50%';
                playButton.style.transform = 'translate(-50%, -50%)';
                playButton.style.width = '60px';
                playButton.style.height = '60px';
                playButton.style.borderRadius = '50%';
                playButton.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
                playButton.style.display = 'flex';
                playButton.style.alignItems = 'center';
                playButton.style.justifyContent = 'center';

                const triangle = document.createElement('div');
                triangle.style.width = '0';
                triangle.style.height = '0';
                triangle.style.borderLeft = '20px solid white';
                triangle.style.borderTop = '12px solid transparent';
                triangle.style.borderBottom = '12px solid transparent';
                triangle.style.marginLeft = '5px';

                playButton.appendChild(triangle);
                wrapper.appendChild(img);
                wrapper.appendChild(playButton);

                video.parentNode?.replaceChild(wrapper, video);
            } catch (error) {
                console.warn(`Failed to replace video with thumbnail: ${posterUrl}`, error);
            }
        })();

        imagePromises.push(promise);
    });

    await Promise.all(imagePromises);

    return element;
};
